/* What /new decides, kept apart from what it draws.
 *
 * The page has one job — get a document into a review — and a handful of states
 * it can be in while doing it. Which state it is in, what each one says, and
 * what a restorable review reads as are all decisions about data, so they are
 * pure functions here and tools/new-review-check.tsx asserts them without a
 * browser.
 */

import type { AnalysisRunState } from '../data/analysisRun';
import { getSeededReport } from '../data/report';
import { getFindings, getReview, listDocuments, listReviews } from '../data/store';
import type { IngestRejection, ReviewRecord } from '../types';

/* ==========================================================================
   The states
   ========================================================================== */

export type NewReviewState =
  /** The dropzone and the example. Nothing else. */
  | 'empty'
  /** Reading and checking, with the named steps in the dropzone's place. */
  | 'working'
  /** The file was refused. The dropzone stays live beneath the reason. */
  | 'refused';

export function stateOf(run: AnalysisRunState | null): NewReviewState {
  if (!run) return 'empty';
  if (run.status === 'rejected') return 'refused';
  return 'working';
}

/**
 * Whether a refusal should offer the example as the way on.
 *
 * A reviewer whose only copy of the plan is a scan cannot fix that here, and
 * telling them to go and find a better file is the whole of the help they get.
 * The example is something they can actually do next. A wrong file type or a
 * file over the limit does not need it: the dropzone below is the answer.
 */
export function offersExample(rejection: IngestRejection): boolean {
  return rejection.reason === 'no-text-layer';
}

/* ==========================================================================
   Reviews this browser can still open
   ========================================================================== */

export interface RestorableReview {
  docId: string;
  fileName: string;
  /** ISO. What "last opened" is measured from. */
  updatedAt: string;
  /** Null where the findings for it are not in this browser. */
  decided: number | null;
  total: number | null;
}

/** "You have a review of doe-example-mv-plan.pdf in this browser." */
export function restoredLine(review: RestorableReview): string {
  return `You have a review of ${review.fileName} in this browser.`;
}

/**
 * "6 of 10 decided · last opened 3 September" — or just the date, where the
 * findings are not in this browser to count against.
 *
 * A count, never a percentage and never a score.
 */
export function restoredMeta(
  review: RestorableReview,
  now: Date = new Date(),
): string {
  const when = lastOpened(review.updatedAt, now);
  if (review.decided === null || review.total === null || review.total === 0) {
    return when;
  }
  return `${review.decided} of ${review.total} decided · ${when}`;
}

/**
 * How long ago, in the words a person uses for it. Anything older than a week
 * gets a date, because "11 days ago" is harder to place than "27 August".
 */
export function lastOpened(iso: string, now: Date = new Date()): string {
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return 'last opened at an unknown time';

  const days = Math.floor((startOfDay(now) - startOfDay(then)) / 86_400_000);
  if (days <= 0) return 'last opened today';
  if (days === 1) return 'last opened yesterday';
  if (days < 7) {
    return `last opened ${then.toLocaleDateString('en-US', { weekday: 'long' })}`;
  }
  return `last opened ${then.toLocaleDateString('en-US', { day: 'numeric', month: 'long' })}`;
}

function startOfDay(d: Date): number {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}

/** Enough about a stored document to offer it, without reading its text. */
export interface StoredDocument {
  id: string;
  fileName: string;
  ingestedAt: string;
}

/**
 * Which reviews this browser can still open, newest first.
 *
 * Two sources, because a review can exist without a document and a document can
 * exist without a review. The worked example has decisions but was never
 * ingested; a plan read five minutes before the reviewer closed the tab has a
 * document but no decisions yet. Both are work in progress and both are
 * offered. Where they are the same review, the review record wins: its
 * updatedAt is when the reviewer last touched it, which is what "last opened"
 * means.
 */
export function restorableFrom(
  reviews: ReviewRecord[],
  documents: StoredDocument[],
  counts: Record<string, { decided: number; total: number }> = {},
): RestorableReview[] {
  const byId = new Map<string, RestorableReview>();

  for (const doc of documents) {
    byId.set(doc.id, {
      docId: doc.id,
      fileName: doc.fileName || doc.id,
      updatedAt: doc.ingestedAt,
      decided: null,
      total: null,
    });
  }

  for (const review of reviews) {
    const existing = byId.get(review.docId);
    byId.set(review.docId, {
      docId: review.docId,
      fileName: review.fileName || existing?.fileName || review.docId,
      updatedAt: review.updatedAt || existing?.updatedAt || '',
      decided: null,
      total: null,
    });
  }

  return [...byId.values()]
    .map((row) => {
      const count = counts[row.docId];
      return count
        ? { ...row, decided: count.decided, total: count.total }
        : row;
    })
    .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
}

/**
 * Every review this browser can still open, with how far each one got.
 *
 * Read here rather than in the two pages that show the list, because /new and
 * the home page must never disagree about how far a review got. The decided
 * count needs the findings, which are stored per document; there are only ever
 * a handful of these, and a count the reviewer can trust is worth the reads.
 *
 * The worked example is the one review whose findings ship with the
 * application instead of being stored, so its count comes from the seed. It
 * has decisions like any other review, and a row that showed only a date for
 * it would be saying less than it knows.
 */
export async function readRestorable(): Promise<RestorableReview[]> {
  const [reviews, documents] = await Promise.all([listReviews(), listDocuments()]);
  const merged = restorableFrom(reviews, documents);

  const counts: Record<string, { decided: number; total: number }> = {};
  await Promise.all(
    merged.map(async (row) => {
      const [findings, review] = await Promise.all([
        getFindings(row.docId),
        getReview(row.docId),
      ]);
      const flags = findings?.flags ?? getSeededReport(row.docId)?.flags;
      if (!flags) return;
      const dispositions = review?.dispositions ?? {};
      counts[row.docId] = {
        total: flags.length,
        decided: flags.filter((f) => dispositions[f.id] !== undefined).length,
      };
    }),
  );

  return restorableFrom(reviews, documents, counts);
}
